// src/pages/Home/ChateauErigoyeSection.jsx
import React, { useContext, useState } from "react";
import { LanguageContext } from "../contexts/LanguageContext";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch (error) {
    return false;
  }
};

const getImageUrl = (url) => {
  if (!url) return "/images/default_logo.webp";

  if (isExternalURL(url)) {
    return url;
  }

  return `${BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

const PRACTICE_PLACES = [
  {
    id: "chateau-erigoye",
    name: "Château Erigoye",
    subtitle: "Beychac-et-Caillau",
    photoUrl:
      "https://lh3.googleusercontent.com/gps-cs-s/APNQkAHhdxSkU0-fqXJMp0x1Y2f-UlMZFv8_NPaWMl2rqx0CKAHTxkHh3wb0PFF_7Lvl-yWlLaoBiNLkBDKkTRps-5pgwNzGNqvQtxzOdNIJp2RFLQmN0DngW9hrnr9fImnAZEOgs-T2qDuzGw0=s680-w680-h510-rw",
    address: "1Bis Rte de la Source, 33750 Beychac-et-Caillau",
    streetAddress: "1Bis Route de la Source",
    postalCode: "33750",
    city: "Beychac-et-Caillau",
    lat: 44.8737259,
    lng: -0.4040191,
    telDisplay: "",
    telLink: "",
    siteUrl: "http://www.chateauerigoye.com/",
    mapsPlaceUrl:
      "https://www.google.com/maps/place/Ch%C3%A2teau+Erigoye/@44.8737259,-0.406594,16z/data=!3m1!4b1!4m9!3m8!1s0xd5530f1d2b0ee59:0xb01f2c9d3105db5b!5m2!4m1!1i2!8m2!3d44.8737259!4d-0.4040191!16s%2Fg%2F11cspym2km?entry=ttu",
  },
  {
    id: "espace-sante-lauduc",
    name: "Espace de Santé Lauduc",
    subtitle: "Pompignac",
    photoUrl:
      "https://img.leboncoin.fr/api/v1/lbcpb1/images/15/bd/fe/15bdfe9f6104727a5832ee8410e33df2ae24f0e3.jpg?rule=ad-large",
    address: "1 avenue du Périgord, 33370 Pompignac",
    streetAddress: "1 avenue du Périgord",
    postalCode: "33370",
    city: "Pompignac",
    lat: 44.864668,
    lng: -0.456684,
    telDisplay: null,
    telLink: null,
    siteUrl: null,
    mapsPlaceUrl:
      "https://www.google.com/maps/search/?api=1&query=44.864668,-0.456684",
  },
];

const TRANSLATIONS = {
  FR: {
    pretitle: "Où se déroulent les séances ?",
    title: "Deux lieux de pratique",
    intro:
      "Les séances de Zen Shiatsu peuvent se dérouler au Château Erigoye ou à l’Espace de Santé Lauduc.",
    addressLabel: "Adresse",
    phoneLabel: "Téléphone",
    websiteLabel: "Site web",
    ctaCall: "Appeler",
    ctaSite: "Voir le site",
    ctaItinerary: "Itinéraire Google Maps",
    ctaShowMap: "Voir sur la carte",
    mapTitle: "Carte du lieu sélectionné",
    openPhoto: "Agrandir la photo",
    close: "Fermer",
    noWebsite: "Site web non renseigné",
  },
  EN: {
    pretitle: "Where are the sessions held?",
    title: "Two practice locations",
    intro:
      "Zen Shiatsu sessions can take place at Château Erigoye or at Espace de Santé Lauduc.",
    addressLabel: "Address",
    phoneLabel: "Phone",
    websiteLabel: "Website",
    ctaCall: "Call",
    ctaSite: "Visit website",
    ctaItinerary: "Google Maps directions",
    ctaShowMap: "View on map",
    mapTitle: "Selected location map",
    openPhoto: "Enlarge photo",
    close: "Close",
    noWebsite: "Website not provided",
  },
};

function getMapEmbedUrl(place) {
  return `https://www.google.com/maps?q=${place.lat},${place.lng}&hl=fr&z=15&output=embed`;
}

function getDirectionsUrl(place) {
  return (
    place.mapsPlaceUrl ||
    `https://www.google.com/maps/search/?api=1&query=${place.lat},${place.lng}`
  );
}

export default function ChateauErigoyeSection() {
  const { language } = useContext(LanguageContext);
  const t = TRANSLATIONS[language] || TRANSLATIONS.FR;

  const [selectedPlaceId, setSelectedPlaceId] = useState(PRACTICE_PLACES[0].id);
  const [lightboxPlace, setLightboxPlace] = useState(null);

  const selectedPlace =
    PRACTICE_PLACES.find((place) => place.id === selectedPlaceId) ||
    PRACTICE_PLACES[0];

  return (
    <section className="relative z-10 w-full px-4 md:px-8 py-10 max-w-[1280px] mx-auto font-satoshi">
      {/* Bandeau titre */}
      <div className="mb-8 text-center">
        <p className="text-sm tracking-widest uppercase text-slate-500 dark:text-slate-400">
          {t.pretitle}
        </p>

        <h2 className="mt-2 text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
          {t.title}
        </h2>

        <p className="mt-3 max-w-2xl mx-auto text-slate-600 dark:text-slate-300">
          {t.intro}
        </p>
      </div>

      {/* Cartes des lieux */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {PRACTICE_PLACES.map((place) => {
          const isSelected = selectedPlaceId === place.id;

          return (
            <article
              key={place.id}
              className={[
                "bg-[#E8BB89] bg-opacity-80 dark:bg-black dark:bg-opacity-50 rounded-2xl shadow p-4 md:p-6 transition-all duration-300",
                isSelected
                  ? "ring-2 ring-primary shadow-xl scale-[1.01]"
                  : "ring-1 ring-transparent hover:ring-primary/40",
              ].join(" ")}
            >
              <div className="relative">
                <img
                  src={getImageUrl(place.photoUrl)}
                  alt={`Photo du lieu ${place.name}`}
                  className="w-full h-64 md:h-80 object-contain rounded-xl shadow cursor-zoom-in transition-transform duration-300 hover:scale-[1.02]"
                  loading="lazy"
                  onClick={() => setLightboxPlace(place)}
                  onError={(e) => {
                    console.error("Image introuvable :", place.photoUrl);
                    e.currentTarget.src = "/images/default_logo.webp";
                  }}
                />

                <button
                  type="button"
                  onClick={() => setLightboxPlace(place)}
                  className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-3 py-1 rounded-full hover:bg-black/70 transition"
                  title={t.openPhoto}
                >
                  {t.openPhoto}
                </button>

                <div className="absolute top-3 left-3 rounded-full bg-white/90 text-slate-900 text-xs font-semibold px-3 py-1 shadow">
                  {place.subtitle}
                </div>
              </div>

              <div className="mt-5 space-y-4 text-slate-800 dark:text-slate-200">
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                    {place.name}
                  </h3>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-600 dark:text-slate-400">
                    {t.addressLabel}
                  </div>
                  <div className="text-lg font-medium">{place.address}</div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {place.telDisplay && place.telLink && (
                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-600 dark:text-slate-400">
                        {t.phoneLabel}
                      </div>
                      <a
                        href={`tel:${place.telLink}`}
                        className="text-lg font-medium underline underline-offset-4 hover:opacity-80"
                      >
                        {place.telDisplay}
                      </a>
                    </div>
                  )}

                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-600 dark:text-slate-400">
                      {t.websiteLabel}
                    </div>

                    {place.siteUrl ? (
                      <a
                        href={place.siteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-lg font-medium underline underline-offset-4 hover:opacity-80 break-all"
                      >
                        {place.siteUrl.replace(/^https?:\/\//, "")}
                      </a>
                    ) : (
                      <span className="text-lg font-medium text-slate-600 dark:text-slate-400">
                        {t.noWebsite}
                      </span>
                    )}
                  </div>
                </div>

                <div className="pt-2 flex flex-wrap gap-3">
                  {place.telDisplay && place.telLink && (
                    <a
                      href={`tel:${place.telLink}`}
                      className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-primary text-white shadow hover:bg-opacity-90 transition"
                    >
                      {t.ctaCall}
                    </a>
                  )}

                  {place.siteUrl && (
                    <a
                      href={place.siteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-white text-slate-900 dark:bg-slate-800 dark:text-white shadow ring-1 ring-slate-200 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition"
                    >
                      {t.ctaSite}
                    </a>
                  )}

                  <a
                    href={getDirectionsUrl(place)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-white text-slate-900 dark:bg-slate-800 dark:text-white shadow ring-1 ring-slate-200 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition"
                  >
                    {t.ctaItinerary}
                  </a>

                  <button
                    type="button"
                    onClick={() => setSelectedPlaceId(place.id)}
                    className={[
                      "inline-flex items-center justify-center px-4 py-2 rounded-xl shadow ring-1 transition",
                      isSelected
                        ? "bg-slate-900 text-white ring-slate-900 dark:bg-white dark:text-slate-900 dark:ring-white"
                        : "bg-white text-slate-900 ring-slate-200 hover:bg-slate-50 dark:bg-slate-800 dark:text-white dark:ring-slate-700 dark:hover:bg-slate-700",
                    ].join(" ")}
                  >
                    {t.ctaShowMap}
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {/* Google Map embed */}
      {/* <div className="mt-8">
        <div className="mb-3 flex flex-col md:flex-row md:items-end md:justify-between gap-2">
          <div>
            <p className="text-sm tracking-widest uppercase text-slate-500 dark:text-slate-400">
              {t.mapTitle}
            </p>

            <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
              {selectedPlace.name}
            </h3>
          </div>

          <a
            href={getDirectionsUrl(selectedPlace)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-primary text-white shadow hover:bg-opacity-90 transition w-fit"
          >
            {t.ctaItinerary}
          </a>
        </div>

        <div className="relative rounded-2xl overflow-hidden shadow ring-1 ring-slate-200 dark:ring-slate-700">
          <iframe
            title={`${selectedPlace.name} - Google Maps`}
            src={getMapEmbedUrl(selectedPlace)}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="w-full h-[360px] md:h-[460px]"
          />
        </div>
      </div> */}

      {/* Lightbox - agrandissement photo */}
      {lightboxPlace && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[1000] flex items-center justify-center p-4"
          onClick={() => setLightboxPlace(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`Photo du lieu ${lightboxPlace.name}`}
        >
          <img
            src={getImageUrl(lightboxPlace.photoUrl)}
            alt={`Photo du lieu ${lightboxPlace.name}`}
            className="max-h-[85vh] max-w-[95vw] object-contain rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onError={(e) => {
              console.error(
                "Image lightbox introuvable :",
                lightboxPlace.photoUrl,
              );
              e.currentTarget.src = "/images/default_logo.webp";
            }}
          />

          <button
            type="button"
            onClick={() => setLightboxPlace(null)}
            className="absolute top-4 right-4 bg-white/90 text-slate-900 rounded-full px-3 py-1 text-sm shadow hover:bg-white transition"
            aria-label={t.close}
            title={t.close}
          >
            {t.close}
          </button>
        </div>
      )}

      {/* SEO JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": PRACTICE_PLACES.map((place) => ({
              "@type": "LocalBusiness",
              name: place.name,
              image: getImageUrl(place.photoUrl),
              address: {
                "@type": "PostalAddress",
                streetAddress: place.streetAddress,
                addressLocality: place.city,
                postalCode: place.postalCode,
                addressCountry: "FR",
              },
              telephone: place.telLink || undefined,
              url: place.siteUrl || undefined,
              geo: {
                "@type": "GeoCoordinates",
                latitude: place.lat,
                longitude: place.lng,
              },
              sameAs: [getDirectionsUrl(place)],
            })),
          }),
        }}
      />
    </section>
  );
}
